/**
 * «Condividi via email» (voce del bottone Condividi, entrambe le vesti): scelta
 * del contenuto, note interne facoltative (solo se il ticket caricato le ha),
 * dettagli del ticket, anteprima live. «Apri nel programma di posta» usa un
 * `mailto:`; «Copia testo» serve a chi usa la posta nel browser o quando il
 * testo e troppo lungo per un mailto.
 * Con `createShareLinks` (adapter admin/rappresentante) la casella «Includi
 * link agli allegati» fa generare al backend link pubblici firmati per gli
 * allegati del contenuto scelto SOLO quando si preme «Apri nel programma di
 * posta» o «Copia testo»: una richiesta (e una voce di cronologia) per azione.
 * Prima, l'anteprima mostra un segnaposto; dopo, il testo effettivamente
 * inviato/copiato finche non si cambiano le opzioni.
 * «Apri con» sceglie il programma: il `mailto:` di sempre oppure Outlook sul
 * web (lavoro o personale) o Gmail in una nuova scheda; l'ultima scelta resta
 * nel browser (localStorage). Se il testo non entra nell'URL della webmail, si
 * copia negli appunti e la webmail si apre con il solo oggetto.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Copy, Mail, X } from 'lucide-react';
import { S3Modal } from '../v3/primitives';
import { useToast } from '../../../contexts/ToastContext';
import { copyToClipboard } from '../shareTicket';
import {
  buildShareEmailText,
  canShareNotes,
  composeHref,
  EMAIL_PROGRAMS,
  fitsCompose,
  isEmailProgram,
  mailtoHref,
  shareableAttachments,
  shareEmailSubject,
  type EmailProgram,
  type ShareEmailContent,
  type ShareEmailTicket,
} from './shareEmailModel';
import type { ShareLinksPayload, ShareLinksResult } from '../../../services/supportApi';
import '../../../styles/admin-v2.css';
import '../../../styles/support-v3.css';

export interface ShareEmailModalProps {
  ticket: ShareEmailTicket;
  onClose: () => void;
  /** Link pubblici firmati agli allegati (adapter operatore). Assente = nessuna casella. */
  createShareLinks?: (payload: ShareLinksPayload) => Promise<ShareLinksResult>;
}

type LinksState = 'idle' | 'loading' | 'error';
type ShareAction = 'mail' | 'copy';

/** Validita dei link nell'etichetta: nota solo dopo la prima generazione. */
const linksValidity = (days: number | null): string => {
  if (days === null) return 'generati all’invio, senza accesso';
  if (days === 0) return 'senza scadenza, senza accesso';
  return `validi ${days} ${days === 1 ? 'giorno' : 'giorni'}, senza accesso`;
};

const CONTENT_OPTIONS: Array<{ value: ShareEmailContent; label: string; sub?: string }> = [
  { value: 'first', label: 'Solo il messaggio iniziale' },
  { value: 'last', label: "Solo l'ultimo messaggio" },
  { value: 'all', label: 'Tutta la conversazione', sub: 'Domande e risposte, in ordine cronologico' },
];

/** Ultimo programma scelto (per browser). Storage non disponibile: programma predefinito. */
export const EMAIL_PROGRAM_KEY = 'unasca.support.shareEmail.program';
const readProgram = (): EmailProgram => {
  try {
    const v = window.localStorage.getItem(EMAIL_PROGRAM_KEY);
    return isEmailProgram(v) ? v : 'default';
  } catch {
    return 'default';
  }
};
const writeProgram = (v: EmailProgram) => {
  try {
    window.localStorage.setItem(EMAIL_PROGRAM_KEY, v);
  } catch {
    // storage pieno o bloccato: la scelta vale solo per questa finestra
  }
};

/** Toast se c'e il provider; senza, la conferma passa da una regione role="status". */
const useToastIfAvailable = (): ReturnType<typeof useToast> | undefined => {
  try {
    return useToast();
  } catch {
    return undefined;
  }
};

const ShareEmailModal: React.FC<ShareEmailModalProps> = ({ ticket, onClose, createShareLinks }) => {
  const toast = useToastIfAvailable();
  const [content, setContent] = useState<ShareEmailContent>('all');
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(true);
  const [note, setNote] = useState<string | null>(null);
  const [program, setProgram] = useState<EmailProgram>(readProgram);
  // Scheda della webmail bloccata dal browser: resta il link da aprire a mano.
  const [blockedHref, setBlockedHref] = useState<string | null>(null);
  const programMeta = EMAIL_PROGRAMS.find((p) => p.value === program) || EMAIL_PROGRAMS[0];
  const notesAvailable = canShareNotes(ticket);
  const effectiveNotes = notesAvailable && includeNotes;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Link agli allegati: generati solo all'azione (una richiesta per azione).
  const canLink = Boolean(createShareLinks);
  const [includeLinks, setIncludeLinks] = useState(true);
  const [linksState, setLinksState] = useState<LinksState>('idle');
  const [linksDays, setLinksDays] = useState<number | null>(null);
  const [partial, setPartial] = useState(false);
  // Testo con i link veri dell'ultima azione, valido finche le opzioni non cambiano.
  const [generated, setGenerated] = useState<{ key: string; body: string } | null>(null);
  const lastAction = useRef<ShareAction>('copy');
  const requests = useMemo(
    () => (canLink ? shareableAttachments(ticket, { content, includeNotes: effectiveNotes }) : []),
    [canLink, ticket, content, effectiveNotes]
  );
  const hasAttachments = requests.length > 0;
  const wantLinks = canLink && includeLinks && hasAttachments;
  const loadingLinks = wantLinks && linksState === 'loading';

  const subject = shareEmailSubject(ticket);
  const baseOptions = useMemo(
    () => ({ content, includeNotes: effectiveNotes, includeDetails, origin }),
    [content, effectiveNotes, includeDetails, origin]
  );
  const optionsKey = `${content}|${effectiveNotes}|${includeDetails}|${wantLinks}`;
  const draft = useMemo(
    () => buildShareEmailText(ticket, { ...baseOptions, linksPlaceholder: wantLinks }),
    [ticket, baseOptions, wantLinks]
  );
  const body = generated && generated.key === optionsKey ? generated.body : draft;
  const long = !fitsCompose(program, subject, body);

  const openWebmail = (href: string) => {
    let win: Window | null = null;
    try {
      // Nuova scheda; l'opener si toglie subito (come `noopener`, ma sapendo se il
      // browser l'ha bloccata: con `noopener` window.open restituisce sempre null).
      win = window.open(href, '_blank');
      if (win) win.opener = null;
    } catch {
      win = null;
    }
    setBlockedHref(win ? null : href);
  };

  const deliver = async (action: ShareAction, text: string) => {
    if (action === 'mail') {
      if (program === 'default') {
        window.location.href = mailtoHref(subject, text);
        return;
      }
      const name = programMeta.action.replace(/^Apri in /, '');
      if (fitsCompose(program, subject, text)) {
        openWebmail(composeHref(program, subject, text));
        return;
      }
      // Troppo lungo per l'URL: testo negli appunti, webmail con il solo oggetto.
      const ok = await copyToClipboard(text);
      openWebmail(composeHref(program, subject, ''));
      setNote(
        ok
          ? `Il testo è troppo lungo per ${name}: è stato copiato negli appunti, incollalo nel corpo del messaggio.`
          : `Il testo è troppo lungo per ${name}: copialo dall’anteprima e incollalo nel corpo del messaggio.`
      );
      return;
    }
    const ok = await copyToClipboard(text);
    if (!ok) {
      setNote('Copia non disponibile: seleziona il testo dell’anteprima e copialo a mano.');
      return;
    }
    if (toast) toast.success('Testo copiato');
    else setNote('Testo copiato');
  };

  const run = async (action: ShareAction) => {
    lastAction.current = action;
    const create = createShareLinks;
    if (!wantLinks || !create) {
      await deliver(action, buildShareEmailText(ticket, baseOptions));
      return;
    }
    setLinksState('loading');
    setPartial(false);
    let res: ShareLinksResult;
    try {
      res = await create({
        attachments: requests.map((r) => ({ messageId: r.messageId, attachmentId: r.attachmentId })),
        includeNotes: effectiveNotes,
      });
    } catch {
      setLinksState('error');
      return;
    }
    const links = res.links || [];
    const text = buildShareEmailText(ticket, { ...baseOptions, links, linksExpireAt: res.expiresAt || null });
    setLinksDays(typeof res.days === 'number' ? res.days : null);
    setPartial(links.length < requests.length);
    setGenerated({ key: optionsKey, body: text });
    setLinksState('idle');
    await deliver(action, text);
  };

  return (
    <S3Modal open onClose={onClose} labelledBy="s4-share-email-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s4-share-email-title">Condividi via email</h3>
          <button type="button" className="s3-pill s3-pill--sm s3-pill--icon" onClick={onClose} aria-label="Chiudi">
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          <fieldset className="s3-field" style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="s3-label" style={{ marginBottom: 5 }}>Contenuto</legend>
            <div className="s3-pick">
              {CONTENT_OPTIONS.map((o) => (
                <label key={o.value} className={`s3-pick__opt${content === o.value ? ' s3-pick__opt--on' : ''}`}>
                  <input
                    type="radio"
                    name="s4-share-email-content"
                    value={o.value}
                    checked={content === o.value}
                    onChange={() => setContent(o.value)}
                  />
                  <span className="s3-pick__txt">
                    {o.label}
                    {o.sub && <span className="s3-pick__sub">{o.sub}</span>}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="s3-pick">
            {notesAvailable && (
              <label className={`s3-pick__opt${includeNotes ? ' s3-pick__opt--on' : ''}`}>
                <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />
                <span className="s3-pick__txt">
                  Includi le note interne
                  <span className="s3-pick__sub">Visibili solo al canale: attenzione a chi inoltri il testo</span>
                </span>
              </label>
            )}
            {canLink && hasAttachments && (
              <label className={`s3-pick__opt${includeLinks ? ' s3-pick__opt--on' : ''}`}>
                <input type="checkbox" checked={includeLinks} onChange={(e) => setIncludeLinks(e.target.checked)} />
                <span className="s3-pick__txt">
                  Includi link agli allegati ({linksValidity(linksDays)})
                  <span className="s3-pick__sub">Chi riceve l’email scarica i file senza entrare nella piattaforma</span>
                </span>
              </label>
            )}
            <label className={`s3-pick__opt${includeDetails ? ' s3-pick__opt--on' : ''}`}>
              <input type="checkbox" checked={includeDetails} onChange={(e) => setIncludeDetails(e.target.checked)} />
              <span className="s3-pick__txt">
                Includi i dettagli del ticket
                <span className="s3-pick__sub">Numero, oggetto, richiedente, canale, stato, priorità, data, assegnati e link</span>
              </span>
            </label>
          </div>

          <div className="s3-field">
            <label className="s3-label" htmlFor="s4-share-email-program">
              Apri con
            </label>
            <select
              id="s4-share-email-program"
              className="s3-sel"
              style={{ width: '100%' }}
              value={program}
              onChange={(e) => {
                const v = e.target.value;
                if (!isEmailProgram(v)) return;
                setProgram(v);
                setBlockedHref(null);
                writeProgram(v);
              }}
            >
              {EMAIL_PROGRAMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div className="s3-field">
            <label className="s3-label" htmlFor="s4-share-email-preview">
              Anteprima · {body.length.toLocaleString('it-IT')} caratteri
            </label>
            <textarea
              id="s4-share-email-preview"
              className="s3-ta"
              value={body}
              readOnly
              rows={10}
              onFocus={(e) => e.currentTarget.select()}
            />
          </div>

          {loadingLinks && (
            <p className="s3-mod__note" role="status">
              Preparazione dei link agli allegati…
            </p>
          )}
          {wantLinks && linksState === 'error' && (
            <p className="s3-mod__warn" role="alert">
              Non è stato possibile preparare i link agli allegati: nulla è stato inviato o copiato.{' '}
              <button type="button" className="s3-pill s3-pill--sm" onClick={() => void run(lastAction.current)}>
                Riprova
              </button>
            </p>
          )}
          {wantLinks && partial && linksState === 'idle' && (
            <p className="s3-mod__warn" role="note">
              Alcuni allegati non sono più disponibili: per quelli nel testo resta il solo nome.
            </p>
          )}
          {long && program === 'default' && (
            <p className="s3-mod__warn" role="note">
              Il testo è lungo: alcuni programmi di posta lo troncano. Usa “Copia testo” e incollalo nel messaggio.
            </p>
          )}
          {long && program !== 'default' && (
            <p className="s3-mod__warn" role="note">
              Il testo è troppo lungo per aprirlo direttamente: “{programMeta.action}” lo copia negli appunti e apre un
              messaggio con il solo oggetto, dove incollarlo. Oppure usa “Copia testo”.
            </p>
          )}
          {blockedHref && (
            <p className="s3-mod__warn" role="alert">
              Il browser ha bloccato la nuova scheda.{' '}
              <a href={blockedHref} target="_blank" rel="noopener noreferrer" onClick={() => setBlockedHref(null)}>
                {programMeta.action}
              </a>
            </p>
          )}
          {note && (
            <p className="s3-mod__note" role="status">
              {note}
            </p>
          )}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={() => void run('copy')} disabled={loadingLinks}>
            <Copy size={13} aria-hidden="true" /> Copia testo
          </button>
          <button type="button" className="s3-pill s3-pill--primary" onClick={() => void run('mail')} disabled={loadingLinks}>
            <Mail size={13} aria-hidden="true" /> {programMeta.action}
          </button>
        </div>
      </div>
    </S3Modal>
  );
};

export default ShareEmailModal;
